import {
  classTypes,
  COMPARISON_ATTRIBUTES_KEYS,
  FILTER_VALUES_KEYS,
  FilterOperationType,
  VISIBLE_FILTERS_KEYS,
} from 'src/lib/constants'
import {
  comparisonAttributeFields,
  ComparisonAttributeItem,
  FilterMenuItems,
} from 'src/redux/slice/SearchCompareData'
import {
  IEnhancedPlanFilterCriteria,
  IEnhancedUnitFilterCriteria,
} from 'src/types/filterPlans.interfaces'
import type { IEnhancedShellFilterCriteria } from 'src/types/filterShells.interfaces.d'

// Visible Filters (Local Storage)
export const saveVisibleFilters = (
  classType: classTypes,
  filters: FilterMenuItems[]
): void => {
  const key = VISIBLE_FILTERS_KEYS[classType]
  localStorage.setItem(key, JSON.stringify(filters))
}

export const loadVisibleFilters = (
  classType: classTypes,
  defaultFilters: FilterMenuItems[]
): FilterMenuItems[] => {
  const key = VISIBLE_FILTERS_KEYS[classType]
  const stored = localStorage.getItem(key)
  if (stored) {
    const parsedFilters = JSON.parse(stored) as FilterMenuItems[]
    // Merge with default filters to handle new filters added to the app
    return mergeFiltersWithDefaults(parsedFilters, defaultFilters)
  }

  return defaultFilters
}

// Filter Values (Session Storage)
export const saveFilterValues = (
  classType: classTypes,
  filters:
    | IEnhancedPlanFilterCriteria
    | IEnhancedShellFilterCriteria
    | IEnhancedUnitFilterCriteria
): void => {
  const key = FILTER_VALUES_KEYS[classType]
  sessionStorage.setItem(key, JSON.stringify(filters))
}

export const loadFilterValues = (
  classType: classTypes
):
  | IEnhancedPlanFilterCriteria
  | IEnhancedShellFilterCriteria
  | IEnhancedUnitFilterCriteria
  | null => {
  const key = FILTER_VALUES_KEYS[classType]
  const stored = sessionStorage.getItem(key)
  if (stored) {
    const parsedFilters = JSON.parse(stored) as
      | IEnhancedPlanFilterCriteria
      | IEnhancedShellFilterCriteria
      | IEnhancedUnitFilterCriteria

    if (classType === classTypes.SINGLE) {
      const parsedPlanFilters = parsedFilters as IEnhancedPlanFilterCriteria

      if (
        parsedPlanFilters.garageLoad?.operation === FilterOperationType.EQUALS
      ) {
        const migratedFilters: IEnhancedPlanFilterCriteria = {
          ...parsedPlanFilters,
          garageLoad: {
            ...parsedPlanFilters.garageLoad,
            operation: FilterOperationType.CONTAINS,
          },
        }

        sessionStorage.setItem(key, JSON.stringify(migratedFilters))
        return migratedFilters
      }
    }

    return parsedFilters
  }
  return null
}

export const clearFilterValues = (classType: classTypes): void => {
  const key = FILTER_VALUES_KEYS[classType]
  sessionStorage.removeItem(key)
}

// Helper function to merge stored filters with defaults.
// Driven by the defaults, so an item added to the app since the user's stored
// copy was written still shows up; the stored `enabled` choice is carried over
// only while the item still applies to the same class types.
const mergeFiltersWithDefaults = <T extends FilterMenuItems>(
  storedFilters: T[],
  defaultFilters: T[]
): T[] => {
  const storedMap = new Map(storedFilters.map((f) => [f.id, f]))

  return defaultFilters.map((defaultFilter) => {
    const storedFilter = storedMap.get(defaultFilter.id)
    if (
      storedFilter &&
      storedFilter.appliedTo.length === defaultFilter.appliedTo.length &&
      storedFilter.appliedTo.every((type) =>
        defaultFilter.appliedTo.includes(type)
      )
    ) {
      return { ...defaultFilter, enabled: storedFilter.enabled }
    }
    return defaultFilter
  })
}

// Load comparison attributes from session storage if available
export const loadComparisonAttributes = (
  classType: classTypes
): ComparisonAttributeItem[] => {
  const key = COMPARISON_ATTRIBUTES_KEYS[classType]
  const defaultAttributes = comparisonAttributeFields.filter((attr) =>
    attr.appliedTo.includes(classType)
  )
  const stored = sessionStorage.getItem(key)
  if (stored) {
    const parsedAttributes = JSON.parse(stored) as ComparisonAttributeItem[]
    // Merge with the defaults the same way visible filters do. Returning the
    // stored array verbatim would hide any attribute added to the app since the
    // user's session began, so newly added ones never reached the Compare screen
    // or its exports until session storage was cleared.
    return mergeFiltersWithDefaults(parsedAttributes, defaultAttributes)
  }

  return defaultAttributes
}

// Save comparison attributes to session storage
export const saveComparisonAttributes = (
  classType: classTypes,
  attributes: ComparisonAttributeItem[]
): void => {
  const key = COMPARISON_ATTRIBUTES_KEYS[classType]
  sessionStorage.setItem(key, JSON.stringify(attributes))
}
